

















































































































































































































































































































import { Component, Vue, Prop, PropSync, Watch, InjectReactive } from 'vue-property-decorator';
import { GQL_WorkRelationEmployeeSave, GQL_AltinnEmploymentTypeEnum, GQL_AltinnShiftTypeEnum, GQL_AltinnEmploymentDurationTypeEnum, GQL_AltinnEmploymentEndReasonEnum } from '@/queries/global';
import { IRules, sValidation } from '@/components/UI/Form/ValidationModule';
import { validateRequired, validateType, validateBetween } from '@/components/UI/Form/Validations';
import { SmartQuery } from 'vue-apollo-decorator';
import { ALL_SUBUNITS } from '@/queries/SubUnit';
import { GQL_currentUnitSubUnits, GQL_currentUnitSubUnits_currentUnit_subUnits } from '@/queries/__generated__/currentUnitSubUnits';
import { ALL_OCCUPATIONS } from '@/queries/Occupation';
import { GQL_occupations, GQL_occupations_currentUnit_occupations } from '@/queries/__generated__/occupations';
import Altinn from '@/app/Altinn';
import WorkRelationLeaveAndLayoffForm from './WorkRelationLeaveAndLayoffForm.vue';
import ValueConfirmation from '@/components/ValueConfirmation.vue';

import dayjs from '@/utils/dayjs';

const validateNoOverlappingPeriods: sValidation = ({ dataValue, formElements, uid }) => {
  const inForeverLand = '9999-12-31';
  if (!dataValue) return true;
  const date = dayjs.strict(dataValue);

  if (!formElements?.leavesAndLayoffs?.startDate) return true;
  if (!formElements?.leavesAndLayoffs?.endDate) return true;

  return Object.keys(formElements.leavesAndLayoffs.startDate)
    .map(uid => {
      return {
        uid: uid,
        startDate: formElements?.leavesAndLayoffs?.startDate[uid]?.dataValue,
        endDate: (formElements?.leavesAndLayoffs?.endDate[uid]?.dataValue || inForeverLand),
      };
    })
    .filter(p => p.uid !== uid) // return true if it's the same period
    .filter(p => !!p.startDate && !!p.endDate) // return true if period doesn't have dates
    .every(p => !date.isBetween(p.startDate, p.endDate, 'day', '[]')); // return true if date doesn't overlap
};

const validateOnlyOneEmptyEndDate: sValidation = ({ formElements, uid }) => {
  const emptyEndDateUid = [];

  for (const key in formElements.leavesAndLayoffs.endDate) {
    if ((formElements?.leavesAndLayoffs?.endDate[key]?.dataValue || '').length === 0) {
      emptyEndDateUid.push(key);
    }
  }
  if (emptyEndDateUid.length <= 1) return true;
  if (uid === undefined) return true;
  return !emptyEndDateUid.includes(uid);
};

@Component({
  components: {
    WorkRelationLeaveAndLayoffForm,
    ValueConfirmation,
  },
})
export default class WorkRelationForm extends Vue {
  @Prop({ type: Object, required: true }) private workRelation!: GQL_WorkRelationEmployeeSave;
  @PropSync('valid', { type: Boolean, default: true }) private validFormSync!: boolean;

  @InjectReactive() private editable!: boolean;

  @SmartQuery<WorkRelationForm, GQL_currentUnitSubUnits>({
    query: ALL_SUBUNITS,
    update: data => data.currentUnit.subUnits,
  }) private subUnits!: GQL_currentUnitSubUnits_currentUnit_subUnits[];

  @SmartQuery<WorkRelationForm, GQL_occupations>({
    query: ALL_OCCUPATIONS,
    update: data => data.currentUnit.occupations,
  }) private occupations!: GQL_occupations_currentUnit_occupations[];

  private shiftTypes = Object.keys(GQL_AltinnShiftTypeEnum);
  private employmentTypes = Object.keys(GQL_AltinnEmploymentTypeEnum);
  private endReasons = Object.keys(GQL_AltinnEmploymentEndReasonEnum);
  private durationTypes = Object.keys(GQL_AltinnEmploymentDurationTypeEnum);
  private formValid = true;
  private startDateConfirmed = true;

  private rules: IRules = {
    subUnitId: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: i18n.t('WorkRelation.subUnit') }) } ],
    occupationId: [ { validation: validateRequired(), if: () => !this.isPension, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'occupation') }) } ],
    startDate: [
      { validation: validateRequired(), if: () => !this.isPension, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'startDate') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
    ],
    endDate: [
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
      {
        validation: ({ dataValue, formElements }) => {
          if (!formElements?.startDate?.dataValue || !dataValue) return true;
          return formElements.startDate.dataValue <= dataValue;
        },
        message: i18n => i18n.t('Error.mustBeSameOrAfter', {
          beforeField: this.t_attr('WorkRelation', 'startDate'),
          afterField: this.t_attr('WorkRelation', 'endDate'),
        }),
        triggerBy: [ 'startDate' ],
      },
    ],
    samePositionSince: [
      { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'samePositionSince') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
    ],
    shiftType: [ { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'shiftType') }) } ],
    employmentType: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'employmentType') }) } ],
    employmentPercentage: [
      { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'employmentPercentage') }) },
      { validation: validateBetween(0, 100), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.between', { field: this.t_attr('WorkRelation', 'employmentPercentage'), min: 0, max: 100}) },
      { validation: validateType('unumber'), message: i18n => i18n.t('GQL.Type.unumber') },
    ],
    employmentPercentageUpdated: [
      { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'employmentPercentageUpdated') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
    ],
    hoursEveryWeekWithFullEmployment: [
      { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'hoursEveryWeekWithFullEmployment') }) },
      { validation: validateBetween(0,168), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.between', { field: this.t_attr('WorkRelation', 'hoursEveryWeekWithFullEmployment'), min: 0, max: 168}) },
    ],
    lastSalaryChange: [
      { validation: validateRequired(), if: () => !this.isPension && !this.isFreelance, triggerBy: [ 'employmentType' ], message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'lastSalaryChange') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
    ],
    endReason: [
      {
        validation: validateRequired(),
        if: () => this.workRelation.endDate,
        message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'endReason') }),
        triggerBy: [ 'endDate', 'employmentType' ],
      },
    ],
    durationType: [
      {
        validation: validateRequired(),
        if: () => !this.isPension && !this.isFreelance,
        message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelation', 'durationType') }),
        triggerBy: [ 'employmentType' ],
      },
    ],
    leavesAndLayoffs: {
      description: [
        {
          validation: validateRequired(),
          message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelationLeaveAndLayoff', 'description') }),
        },
      ],
      percentageLeave: [
        { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelationLeaveAndLayoff', 'percentageLeave') }) },
        { validation: validateBetween(0, 100), message: i18n => i18n.t('Error.between', { field: this.t_attr('WorkRelationLeaveAndLayoff', 'percentageLeave'), min: 0, max: 100}) },
        { validation: validateType('unumber'), message: i18n => i18n.t('GQL.Type.unumber') },
      ],
      startDate: [
        {
          validation: validateRequired(),
          message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelationLeaveAndLayoff', 'startDate') }),
        },
        {
          validation: validateNoOverlappingPeriods,
          triggerBy: [ 'leavesAndLayoffs.startDate', 'leavesAndLayoffs.endDate' ],
          message: i18n => i18n.t('PaycheckPeriod.dateCannotOverlap'),
        },
        {
          validation: ({ dataValue, formElements }) => {
            const workRelationStartDate = formElements?.startDate?.dataValue;

            return dayjs.strict(dataValue).isSameOrAfter(workRelationStartDate, 'day');
          },
          message: i18n => i18n.t('Error.mustBeSameOrAfter', {
            beforeField: i18n.t('WorkRelation.itsStartDate'),
            afterField: this.t_attr('WorkRelationLeaveAndLayoff', 'startDate'),
          }),
          triggerBy: [ 'startDate' ],
        },
      ],
      endDate: [
        { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
        {
          validation: ({ dataValue, formElements, uid }) => {
            if (!(formElements.leavesAndLayoffs.startDate[uid as string])?.dataValue || !dataValue) return true;
            return formElements.leavesAndLayoffs.startDate[uid as string].dataValue <= dataValue;
          },
          message: i18n => i18n.t('Error.mustBeSameOrAfter', {
            beforeField: this.t_attr('WorkRelationLeaveAndLayoff', 'startDate'),
            afterField: this.t_attr('WorkRelationLeaveAndLayoff', 'endDate'),
          }),
          triggerBy: [ ({ withSameUID }) => withSameUID('leavesAndLayoffs.startDate') ],
        },
        {
          validation: ({ dataValue, formElements }) => {
            const workRelationEndDate = formElements?.endDate?.dataValue;
            if (!workRelationEndDate) { return true }

            return dayjs.strict(dataValue).isSameOrBefore(workRelationEndDate, 'day');
          },
          message: i18n => i18n.t('Error.mustBeSameOrBefore', {
            beforeField: this.t_attr('WorkRelationLeaveAndLayoff', 'endDate'),
            afterField: i18n.t('WorkRelation.itsEndDate'),
          }),
          triggerBy: [ 'endDate' ],
        },
        {
          validation: ({ dataValue, formElements }) => {
            const workRelationEndDate = formElements?.endDate?.dataValue;
            const leaveEndDate = dataValue;

            // if workRelationEndDate is empty, always return true
            // otherwise, check that leave endDate is present
            return (!workRelationEndDate) || (leaveEndDate);
          },
          triggerBy: [ 'endDate' ],
          message: i18n => i18n.t('WorkRelation.LeavesLayoffs.endDateRequiredForEndedWorkRelation'),
        },
        {
          validation: validateNoOverlappingPeriods,
          triggerBy: [ 'leavesAndLayoffs.startDate', 'leavesAndLayoffs.endDate' ],
          message: i18n => i18n.t('PaycheckPeriod.dateCannotOverlap'),
        },
        {
          validation: validateOnlyOneEmptyEndDate,
          message: i18n => i18n.t('Error.required', { field: this.t_attr('WorkRelationLeaveAndLayoff', 'endDate') }),
        },
      ],
    },
  };

  private get isPension(): boolean {
    return this.workRelation.employmentType === Altinn.pensionEmploymentType;
  }

  private get isFreelance(): boolean {
    return this.workRelation.employmentType === Altinn.freelancerEmploymentType;
  }

  @Watch('isPension')
  private unsetFieldsIfIsPension(): void {
    if (this.isPension) {
      this.workRelation.startDate = null;
      this.workRelation.endDate = null;
      this.workRelation.occupationId = null;
      this.workRelation.samePositionSince = null;
      this.workRelation.shiftType = null;
      this.workRelation.employmentPercentage = null;
      this.workRelation.employmentPercentageUpdated = null;
      this.workRelation.hoursEveryWeekWithFullEmployment = null;
      this.workRelation.lastSalaryChange = null;
      this.workRelation.endReason = null;
      this.workRelation.durationType = null;
    }
  }

  @Watch('isPension')
  private resetPensionEndDate(isPension: boolean) {
    if (!isPension) {
      this.workRelation.pensionEndDate = null;
    }
  }


  @Watch('workRelation.startDate')
  private populateDatesFromStartDate() {
    if (this.isPension || this.isFreelance) return;

    if (!dayjs.strict(this.workRelation.startDate).isValid()) return;

    if (!this.workRelation.samePositionSince) {
      this.workRelation.samePositionSince = this.workRelation.startDate;
    }

    if (!this.workRelation.employmentPercentageUpdated) {
      this.workRelation.employmentPercentageUpdated = this.workRelation.startDate;
    }

    if (!this.workRelation.lastSalaryChange) {
      this.workRelation.lastSalaryChange = this.workRelation.startDate;
    }
  }

  @Watch('workRelation.endDate')
  private emptyEndDateReasonIfNoEndDate(): void {
    if (!this.workRelation.endDate) {
      this.workRelation.endReason = null;
    }
  }

  private printSubUnitName(subUnitId: string): string {
    const subUnit = this.subUnits.find(subUnit => subUnit.id === subUnitId);
    return subUnit?.name || '';
  }

  private printOccupationName(occupationId: string): string {
    const occupation = this.occupations.find(occupation => occupation.id === occupationId);
    return occupation?.name || '';
  }

  private addLeaveAndLayoff(): void {
    if (!this.workRelation.leaveAndLayoffs) this.workRelation.leaveAndLayoffs = [];
    this.workRelation.leaveAndLayoffs.push({
      id: null,
      startDate: null,
      endDate: null,
      percentageLeave: null,
      leaveId: null,
      description: null,
      _destroy: false,
    });
  }

  @Watch('formValid')
  @Watch('startDateConfirmed')
  private setValidFormSync(): void {
    this.validFormSync = this.formValid && this.startDateConfirmed;
  }
}
